/**
 * Production wiring: a single AgentService backed by the live relay (WebSocket) and
 * directory (HTTP). URLs come from Vite env vars with local-dev defaults.
 *
 * Keys are generated and held inside the AgentService (on the device). The directory
 * only ever receives the public, self-signed card.
 */
import type { SignedAgentCard } from "@alinq/proto";
import { AgentService, type DirectoryClient } from "./agentService.js";
import { browserWsTransport } from "./browserTransport.js";

// Fallbacks: local endpoints in dev, the LIVE endpoints in any production build.
// The native (Capacitor) app runs from capacitor://localhost, so a localhost
// fallback there means "talk to the phone itself" — that broke TestFlight
// onboarding with a bare "Load failed". Production must always default to live.
const RELAY_URL =
  (import.meta.env.VITE_RELAY_URL as string | undefined) ??
  (import.meta.env.PROD ? "wss://app.alinq.ai/relay" : "ws://127.0.0.1:8787");
const DIRECTORY_URL =
  (import.meta.env.VITE_DIRECTORY_URL as string | undefined) ??
  (import.meta.env.PROD ? "https://app.alinq.ai/api" : "http://127.0.0.1:8788");

class HttpDirectoryClient implements DirectoryClient {
  constructor(private readonly baseUrl: string) {}

  async register(card: SignedAgentCard): Promise<void> {
    const res = await fetch(`${this.baseUrl}/cards/${encodeURIComponent(card.handle)}`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(card),
    });
    if (!res.ok) throw new Error(`directory rejected the card (${res.status})`);
  }

  async resolveHandle(handle: string): Promise<SignedAgentCard | undefined> {
    const res = await fetch(`${this.baseUrl}/cards/${encodeURIComponent(handle)}`);
    return res.ok ? ((await res.json()) as SignedAgentCard) : undefined;
  }

  async resolveDid(did: string): Promise<SignedAgentCard | undefined> {
    const res = await fetch(`${this.baseUrl}/cards-by-did/${encodeURIComponent(did)}`);
    return res.ok ? ((await res.json()) as SignedAgentCard) : undefined;
  }
}

export const RELAY_ENDPOINT = RELAY_URL;
export const DIRECTORY_ENDPOINT = DIRECTORY_URL;

export const agent = new AgentService({
  directory: new HttpDirectoryClient(DIRECTORY_URL),
  connectToRelay: () => browserWsTransport(RELAY_URL),
});
